import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmpresaEnUnDiaService } from 'src/app/shared/services/empresa-en-un-dia.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { gtpRutValidator } from 'src/app/shared/validators/rut-validator';
import { AlertService } from '../../_alert/alert.service';
import { IEmpresaEnUnDia } from 'src/app/models/empresa-en-un-dia/empresa-en-un-dia.model';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { AsincronoService } from 'src/app/shared/services/asincrono.service';

export interface InputModel {
  tipoReporte?: string,
  rut?: string;
}

@Component({
  selector: 'app-mat-modal-crear-reporte-empresa-en-un-dia',
  templateUrl: './mat-modal-crear-reporte-empresa-en-un-dia.component.html',
  styleUrls: ['./mat-modal-crear-reporte-empresa-en-un-dia.component.scss']
})
export class MatModalCrearReporteEmpresaEnUnDiaComponent {
  inputModel: InputModel | any = {
    rut: '',
    tipoReporte: ''
  };

  public tipoReporteOptions: {displayName: string, value: string}[] = [
    {displayName: "Reporte Certificados Empresa en un dia", value: 'certificados'}, 
    {displayName: "Reporte Actuaciones Empresa en un dia", value: 'actuacion'}
  ];
  public crearForm: UntypedFormGroup;
  private subscripciones: Subscription = new Subscription();

  private readonly CERTIFICATE_TYPE = {
    VIGENCIA: "CertificadoDeVigencia",
    ESTATUTOS: "CertificadoDeEstatutos",
    ANOTACIONES: "CertificadoDeAnotaciones"
  } as const;
  
  constructor(public dialogRef: MatDialogRef<MatModalCrearReporteEmpresaEnUnDiaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InputModel, 
      private formBuilder: UntypedFormBuilder,
      private empresaEnUnDiaService: EmpresaEnUnDiaService,
      private utilsService: UtilsService,
      private alertService: AlertService,
      private spinner: NgxSpinnerService,
      private asyncService: AsincronoService
    ) {
    this.inputModel = data;
    this.crearForm = this.formBuilder.group({
      tipoReporte: [this.inputModel.tipoReporte || ''],
      rut: [this.inputModel.rut || '', [Validators.required, gtpRutValidator()]]
    }) as FormGroupTyped<any>;
  }

  onSubmit(): void {
    if (!this.crearForm.valid) {
      this.utilsService.validateAllFormFields(this.crearForm);
      this.alertService.error('Complete correctamente los campos para crear el reporte.');
      return;
    }

    const rutReporte = this.utilsService.formatterRutV2(this.crearForm.get('rut')?.value || '');
    const tipoReporte = this.crearForm.get('tipoReporte')?.value || '';
    if(tipoReporte === 'certificados') {
      this.crearReporteCertificados(rutReporte, tipoReporte);
    } else if(tipoReporte == 'actuacion') {
      this.crearReporteActuaciones(rutReporte, tipoReporte);
    }
  }

  crearReporteCertificados(rutReporte: string, tipoReporte: string) {
    this.spinner.show();
    this.subscripciones.add(this.empresaEnUnDiaService.createReport(rutReporte).subscribe(
      (data: IEmpresaEnUnDia) => {

        if (!this.certificatesAreReady(data)) {
          this.alertService.warn('Se esta ejecutando la creación del reporte, cuando este finalizado se le notificará.');
          this.asyncService.llamadaEmpresaEnUnDiaV2(data?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud?.IDTransaccion, rutReporte);
          this.spinner.hide();
          this.close({data, rutReporte, tipoReporte, completed: false});
          return;
        }
        
        this.spinner.hide();
        this.alertService.success('Se ha creado exitosamente el reporte.');
        this.close({data, rutReporte, tipoReporte, completed: true});
      },
      ({ error }) => {
        this.alertService.error(error.message || 'Ha ocurrido un error al crear el reporte.');
        this.spinner.hide();
      }
    ));
  }

  crearReporteActuaciones(rutReporte: string, tipoReporte: string) {
    this.spinner.show();
    this.subscripciones.add(this.empresaEnUnDiaService.createReportActuaciones(rutReporte).subscribe(
      (data: IEmpresaEnUnDia) => {

        if (data?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud?.EstadoTransaccion === 'ERROR') {
          this.alertService.error("Ocurrio un error al intentar crear el reporte.");
          this.spinner.hide();
          return;
        }

        if (data?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud?.EstadoTransaccion != 'COMPLETED') {
          this.alertService.warn('Se esta ejecutando la creación del reporte, cuando este finalizado se le notificará.');
          this.asyncService.llamadaEmpresaEnUnDiaActuacion(data?.EmpresaEnUnDiaReporte?.DatosBasicosSolicitud?.IDTransaccion, rutReporte);
          this.spinner.hide();
          this.close({data, rutReporte, tipoReporte, completed: false});
          return;
        }

        this.spinner.hide();
        this.alertService.success('Se ha creado exitosamente el reporte.');
        this.close({data, rutReporte, tipoReporte, completed: true});
      },
      ({ error }) => {
        this.alertService.error(error.message || 'Ha ocurrido un error al crear el reporte.');
        this.spinner.hide();
      }
    ));
  }

  /*certificatesAreReady(data: IEmpresaEnUnDia): boolean {
    if (!data.EmpresaEnUnDiaReporte.Certificados) return false;
    if (data.EmpresaEnUnDiaReporte.Certificados.length === 0) return false;

    for (const certificado of data.EmpresaEnUnDiaReporte.Certificados) {
      if (!(this.CERTIFICATE_TYPE.VIGENCIA in certificado)) return false;
      if (!(this.CERTIFICATE_TYPE.ANOTACIONES in certificado)) return false;
      if (!(this.CERTIFICATE_TYPE.ESTATUTOS in certificado)) return false;
    }

    return true;
  }*/

  certificatesAreReady(data: IEmpresaEnUnDia): boolean {
    if (!data.EmpresaEnUnDiaReporte.Certificados) return false;
    if (data.EmpresaEnUnDiaReporte.Certificados.length === 0) return false;

    const certificados = data?.EmpresaEnUnDiaReporte?.Certificados;

    const hasVigencia = certificados.some(c => this.CERTIFICATE_TYPE.VIGENCIA in c);
    const hasAnotaciones = certificados.some(c => this.CERTIFICATE_TYPE.ANOTACIONES in c);
    const hasEstatutos = certificados.some(c => this.CERTIFICATE_TYPE.ESTATUTOS in c);

    return hasVigencia && hasAnotaciones && hasEstatutos;
  }

  close(data: any = null) {
    this.dialogRef.close(data);
  }

  ngOnDestroy(): void {
    this.subscripciones.unsubscribe();
  }

}
